import { Component } from '@/types'
import barba from '@barba/core'
import { isLoading } from './loading-indicator'
import {
  canAddToCart,
  cartAlreadyHasPreorder,
  isPreorderProduct,
} from '@/utils/cart-validation'
import { showCartValidationModal } from './cart-validation-modal'
import { showMultiPreorderNoticeModal } from './multi-preorder-notice-modal'

type PreorderEntry = { is_preorder: boolean; label: string }
type PreorderMap = Record<string, PreorderEntry>

function setUrlParam(key, value) {
  const url = new URL(window.location.href)

  if (value === null) {
    url.searchParams.delete(key)
  } else {
    url.searchParams.set(key, value)
  }

  // Preserve the current entry's data (e.g. `{overlay: true}`) when replacing.
  // Without this, changing variant inside a quick-view overlay drops the
  // overlay flag, and clicking the X then fails to close the overlay and the
  // PDP loads as a standalone page instead.
  const currentEntry = barba.history.get(barba.history.size - 1)
  const data = currentEntry?.data || {}

  barba.history.add(url.href, 'popstate', 'replace', data)
}

function defaultParser(form) {
  const formData = new FormData(form)
  const [id] = formData.getAll('id')
  const quantity = parseInt(formData.getAll('quantity').toString() || '1')

  return {
    items: [{ id, quantity }],
  }
}

function pdpParser(form, productData) {
  const formData = new FormData(form)
  const formValues = Object.fromEntries(formData.entries())

  const variant =
    formValues.id ||
    productData.variants.find(variant => {
      return variant.options.every((value, idx) => {
        const optionName = productData.options[idx].name
        return formValues[optionName] === value
      })
    })?.id

  const quantity = parseInt(formData.getAll('quantity').toString() || '1')

  return {
    items: [{ id: variant, quantity }],
  }
}

async function addToCart(data) {
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })

    return await res.json()
  } catch (error) {
    console.log(`Couldn't add to card: `, error)
  }
}

function disableBuyButtonIfOutOfStock(parser, form, productData) {
  const id = parser(form, productData).items[0].id
  const { available } = productData.variants.find(
    variant => variant.id === parseInt(id.toString())
  )

  const submitButton = form.querySelector('[type="submit"]')
  submitButton.disabled = !available
}

function getPreorderMap(form: HTMLFormElement): PreorderMap {
  const root = form.closest('.product-card, .main-product') || document
  const script = root.querySelector(
    'script[data-preorder-variants]'
  ) as HTMLScriptElement | null

  if (!script?.textContent) return {}

  try {
    return JSON.parse(script.textContent) as PreorderMap
  } catch {
    return {}
  }
}

function updatePreorderUI(form: HTMLFormElement, variantId: string | number) {
  const map = getPreorderMap(form)
  const entry = map[String(variantId)]
  const isPreorder = !!entry?.is_preorder

  form.dataset.isPreorder = isPreorder ? 'true' : 'false'

  const notice = form.querySelector(
    '[data-ref="preorder-notice"]'
  ) as HTMLElement | null
  if (notice) notice.hidden = !isPreorder

  const label = form.querySelector(
    '[data-ref="add-to-cart-label"]'
  ) as HTMLElement | null
  if (!label) return

  // Cache the original label once so we can restore it.
  if (label.dataset.originalLabel === undefined) {
    label.dataset.originalLabel = label.textContent?.trim() || ''
  }

  if (isPreorder) {
    const prefix = label.dataset.preorderPrefix || 'PRE-ORDER'
    label.textContent = entry.label ? `${prefix}: ${entry.label}` : prefix
  } else {
    label.textContent = label.dataset.originalLabel
  }
}

const component: Component = ref => {
  if (!ref.productForm) return

  ref.productForm.forEach(async form => {
    let productData = null

    if (form.dataset.pdp) {
      const res = await fetch(window.location.pathname + '.js')
      productData = await res.json()
    }

    const parser = form.dataset.pdp !== undefined ? pdpParser : defaultParser

    // Initial preorder UI state for the currently-selected variant.
    if (form.dataset.pdp) {
      const initialId = parser(form, productData).items[0].id
      if (initialId) updatePreorderUI(form, initialId)
    }

    // update url when interactive with the form
    form.addEventListener('change', () => {
      const id = parser(form, productData).items[0].id

      setUrlParam('variant', id)

      window.dispatchEvent(
        new CustomEvent('se-event.product.variant-updated', { detail: id })
      )

      if (productData) {
        disableBuyButtonIfOutOfStock(parser, form, productData)
        updatePreorderUI(form, id)
      }
    })

    form.addEventListener('submit', async e => {
      e.preventDefault()

      // Resolve current variant id and re-sync dataset against the preorder
      // map. Without this, a fast click before the change handler fires uses
      // a stale dataset and validation lets a pre-order variant slip into a
      // cart with regular items (or vice versa).
      if (productData) {
        const currentId = parser(form, productData).items[0].id
        if (currentId) updatePreorderUI(form, currentId)
      }

      const isPreorder = isPreorderProduct(form as HTMLFormElement)
      const pendingAdd = parser(form, productData)

      if (!canAddToCart(isPreorder)) {
        // Mixing pre-order and in-stock isn't allowed. Offer the customer the
        // choice to keep the current cart (cancel this add) or empty it and
        // add this item instead.
        showCartValidationModal({ pendingAdd })
        return
      }

      // Capture the pre-order state of the cart BEFORE we add, so we can
      // show the multi-preorder notice after a successful add if this is
      // the 2nd (or later) pre-order line.
      const shouldShowMultiPreorderNotice =
        isPreorder && cartAlreadyHasPreorder()

      isLoading('product-form', true)

      const result = await addToCart(pendingAdd)

      if (result.message) {
        console.log(result.message)
      } else {
        window.dispatchEvent(
          new CustomEvent('se-event.cart.product-added', { detail: result })
        )
        if (shouldShowMultiPreorderNotice) {
          showMultiPreorderNoticeModal()
        }
      }

      isLoading('product-form', false)
    })
  })
}

export default component
